/**
 * Saha detayının OKUMA bloğu — değerler, bağlantı geçmişi, sinyaller.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * NEDEN AYRI SAYFA DEĞİL, AYNI SAYFANIN ÜST BLOĞU
 *
 * Bir sahanın iki ekranı olsaydı ("okumalar" ve "yapılandırma") kullanıcı
 * "bu sahadan neden veri gelmiyor?" sorusunu cevaplamak için ikisi arasında
 * gidip gelirdi — cevap ikisinin KESİŞİMİNDE: değer eski mi, pano yoklaması
 * açık mı, adres doğru mu. Tek sayfa; önce ölçülen, sonra ayarlanan.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { Badge } from '../design/Status.tsx';
import { Card, CardBody, CardHeader } from '../design/Card.tsx';
import {
  DURUM_SUNUMU,
  ODORANT_ETIKETI,
  SICAKLIK_ETIKETI,
  gecenSure,
  sinyalAdi,
  tankDolulugu,
  useSahaOkumasi,
  type OkumaDetayi,
} from './okuma.ts';
import { Skeleton, SkeletonList } from '../design/Feedback.tsx';
import { lazy, Suspense } from 'react';

/*
 * ─── GRAFİK TEMBEL YÜKLENİYOR ────────────────────────────────────────────────
 * Recharts ana pakete girseydi, grafiğe hiç bakmayan kullanıcı da o yükü
 * indirirdi. Ölçüm Görev 6'da yapıldı (rakamlar STATUS'ta); haritayla aynı
 * gerekçe — mobil öncelikli üründe ana ekranın ağırlığı açılış süresidir.
 */
const TankGrafik = lazy(() => import('./TankGrafik.tsx').then((m) => ({ default: m.TankGrafik })));

export function SahaOkumasi({ sahaId }: { readonly sahaId: number }): React.JSX.Element | null {
  const sorgu = useSahaOkumasi(sahaId);

  if (sorgu.isPending) return <SkeletonList rows={2} />;
  /*
   * Okuma bloğu YÜKLENEMEZSE sayfa çökmüyor: yapılandırma bilgisi hâlâ
   * değerli ve altta duruyor. Sessizce boş bırakmıyoruz ama tüm sayfayı da
   * hataya çevirmiyoruz.
   */
  if (sorgu.isError) {
    return (
      <Card>
        <CardBody>
          <p role="alert" className="text-sm text-danger">
            Okuma verisi yüklenemedi. Aşağıdaki yapılandırma bilgisi geçerli.
          </p>
        </CardBody>
      </Card>
    );
  }

  return <Icerik veri={sorgu.data} />;
}

function Icerik({ veri }: { readonly veri: OkumaDetayi }): React.JSX.Element {
  const { panels, tanks, signals, now } = veri;

  return (
    <div className="flex flex-col gap-4">
      {/* ── Tanklar: asıl merak edilen değer ─────────────────────────── */}
      {tanks.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-ink">Tank ölçümleri</h2>
          </CardHeader>
          <CardBody>
            <ul className="flex flex-col gap-4">
              {tanks.map((tank) => {
                const doluluk = tankDolulugu(tank);
                const yas = gecenSure(tank.read_at, now);
                return (
                  <li key={tank.tank_id}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium text-ink">Tank {tank.tank_number ?? '—'}</span>
                      <span className="text-sm text-ink-muted">
                        {tank.capacity_m3} m³
                        {tank.nominal_kg !== null && ` · ${tank.nominal_kg} kg dolu`}
                      </span>
                    </div>

                    {doluluk.yuzde === null ? (
                      <p className="mt-1 text-sm text-ink-muted">Bu tanktan henüz ölçüm yok.</p>
                    ) : (
                      <>
                        <div className="mt-1 flex items-baseline gap-3">
                          <span className="text-2xl font-semibold text-ink">
                            %{doluluk.yuzde.toFixed(1)}
                          </span>
                          {doluluk.kg !== null && (
                            <span className="text-sm text-ink-muted">≈ {doluluk.kg} kg</span>
                          )}
                          {tank.pressure_bar !== null && (
                            <span className="text-sm text-ink-muted">
                              {tank.pressure_bar.toFixed(2)} bar
                            </span>
                          )}
                        </div>
                        <div
                          className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
                          aria-hidden="true"
                        >
                          <div
                            className="h-full rounded-full bg-brand-500"
                            style={{
                              width: `${String(Math.min(Math.max(doluluk.yuzde, 0), 100))}%`,
                            }}
                          />
                        </div>
                      </>
                    )}

                    {/*
                     * ÖLÇÜMÜN YAŞI DEĞERİN YANINDA. Üç gün önceki bir seviye
                     * bugünkü gibi gösterilirse kullanıcı ona göre karar verir
                     * — dolum planlaması yanlış yapılır.
                     */}
                    {yas !== null && <p className="mt-1 text-xs text-ink-muted">Ölçüm: {yas}</p>}

                    {/*
                     * Geçmiş grafiği yalnız ÖLÇÜMÜ OLAN tankta: hiç ölçümü
                     * olmayan tankta boş bir grafik açmak "veri var ama
                     * çizilemiyor" izlenimi verirdi.
                     */}
                    {tank.read_at !== null && (
                      <div className="mt-3">
                        <Suspense fallback={<Skeleton className="h-56 w-full" />}>
                          <TankGrafik
                            tankId={tank.tank_id}
                            tankAdi={`Tank ${String(tank.tank_number ?? '')}`.trim()}
                          />
                        </Suspense>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>
      )}

      {/* ── Panolar: bağlantı durumu ve site düzeyi değerler ──────────── */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-ink">Bağlantı durumu</h2>
        </CardHeader>
        <CardBody>
          {panels.length === 0 ? (
            <p className="text-sm text-ink-muted">Bu sahada pano tanımlı değil.</p>
          ) : (
            <ul className="flex flex-col gap-4">
              {panels.map((pano) => {
                const durum = DURUM_SUNUMU[pano.data_state];
                const sonVeri = gecenSure(pano.last_ok_at, now);
                const sonDeneme = gecenSure(pano.last_attempt_at, now);
                return (
                  <li key={pano.panel_id}>
                    <div className="flex items-start justify-between gap-3">
                      <span className="font-medium text-ink">{pano.serial_number}</span>
                      <Badge tone={durum.tone}>{durum.etiket}</Badge>
                    </div>
                    {durum.ipucu !== undefined && (
                      <p className="mt-0.5 text-sm text-ink-muted">{durum.ipucu}</p>
                    )}

                    <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted">
                      <div className="flex gap-1">
                        <dt>Son veri:</dt>
                        <dd className="font-medium text-ink">{sonVeri ?? 'hiç'}</dd>
                      </div>
                      <div className="flex gap-1">
                        <dt>Son deneme:</dt>
                        <dd className="font-medium text-ink">{sonDeneme ?? 'hiç'}</dd>
                      </div>
                      {pano.consecutive_failures > 0 && (
                        <div className="flex gap-1">
                          <dt>Ardışık hata:</dt>
                          <dd className="font-medium text-ink">{pano.consecutive_failures}</dd>
                        </div>
                      )}
                      {pano.temperature_c !== null && (
                        <div className="flex gap-1">
                          <dt>{SICAKLIK_ETIKETI}:</dt>
                          <dd className="font-medium text-ink">
                            {pano.temperature_c.toFixed(1)} °C
                          </dd>
                        </div>
                      )}
                      {/*
                       * `odor` GAZ KAÇAĞI DEĞİL — odorant tankının seviyesi
                       * (şema notu). Yanlış etiket kullanıcıyı sahaya
                       * koşturabilirdi.
                       */}
                      {pano.odor_ppm !== null && (
                        <div className="flex gap-1">
                          <dt>{ODORANT_ETIKETI}:</dt>
                          <dd className="font-medium text-ink">{pano.odor_ppm.toFixed(1)}</dd>
                        </div>
                      )}
                    </dl>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* ── Sinyaller ────────────────────────────────────────────────── */}
      {signals.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-ink">Sinyaller</h2>
          </CardHeader>
          <CardBody>
            <ul className="flex flex-col gap-3">
              {signals.map((sinyal) => (
                <li
                  key={`${sinyal.panel_serial}-${sinyal.kind}-${String(sinyal.tank_id)}`}
                  className="flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-ink">{sinyalAdi(sinyal.kind)}</span>
                    <span className="block text-xs text-ink-muted">
                      Değişim: {gecenSure(sinyal.last_change_at, now) ?? '—'}
                    </span>
                    {/*
                     * DOĞRULANMAMIŞ EŞLEME AÇIKÇA SÖYLENİYOR (S39).
                     * Gizlemek bilgi kaybı, teyit edilmiş gibi göstermek yalan
                     * olurdu — [K-017] bu bayrağı tam bu yüzden taşıyor.
                     */}
                    {!sinyal.verified && (
                      <span className="block text-xs text-warn">⚠ Eşleme cihazda doğrulanmadı</span>
                    )}
                  </div>
                  <Badge tone={sinyal.value ? 'warn' : 'neutral'}>
                    {sinyal.value ? 'Aktif' : 'Pasif'}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
